// Touch gestures on the AR overlay (2026-10-06, iPhone feedback: "click
// and hold on the asset overlay and drag it ... a rotary two-touch action
// could change the direction"). Pure - no DOM - so it runs under plain Node
// (frontend/tests/arGesture.test.ts). ar.ts feeds it pointer events and a
// timer tick and acts on what comes out:
//
//   one finger, lifted quickly without moving        -> "tap"   (identify, as before)
//   one finger, held still HOLD_MS                   -> "hold"  (haptic tick / visual ring)
//     ... then moved                                 -> "drag" (the overlay follows) ... "drag-end"
//   one finger moved before HOLD_MS                  -> nothing (a swipe, not a tap or a drag)
//   two fingers                                      -> "rotate-start", "rotate" (twist degrees,
//                                                       clockwise +), "rotate-end"; the pinch
//                                                       distance is ignored (no scaling)
//   a second finger during a drag                    -> "cancel" (the drag), then the twist
//
// Screen coordinates are CSS px, y down, so a clockwise twist on screen is a
// positive angle.

export const HOLD_MS = 350;
/** movement that still counts as "not moved" for a tap / before the hold fires */
export const SLOP_PX = 10;
/** a twist smaller than this on release is not a rotation */
export const MIN_TWIST_DEG = 1.5;

export type GestureEvent =
  | { type: "tap"; x: number; y: number }
  | { type: "hold"; x: number; y: number }
  | { type: "drag"; x: number; y: number; x0: number; y0: number }
  | { type: "drag-end"; x: number; y: number; x0: number; y0: number }
  | { type: "rotate-start"; cx: number; cy: number }
  | { type: "rotate"; deg: number; cx: number; cy: number }
  | { type: "rotate-end"; deg: number; cx: number; cy: number }
  | { type: "cancel" };

type State = "idle" | "press" | "swipe" | "drag" | "rotate" | "done";

interface Pt {
  x: number;
  y: number;
}

function wrap180(d: number): number {
  return ((((d + 180) % 360) + 360) % 360) - 180;
}

export class GestureClassifier {
  state: State = "idle";
  private pts = new Map<number, Pt>();
  private start: Pt & { t: number } = { x: 0, y: 0, t: 0 };
  private dragged = false;
  private rot = { a0: 0, last: 0, total: 0, ids: [0, 0] as [number, number] };

  holdMs: number;
  slop: number;

  constructor(holdMs = HOLD_MS, slop = SLOP_PX) {
    this.holdMs = holdMs;
    this.slop = slop;
  }

  /** a finger touched down at time `t` (ms) */
  down(id: number, x: number, y: number, t: number): GestureEvent[] {
    this.pts.set(id, { x, y });
    const out: GestureEvent[] = [];
    if (this.pts.size === 1 && (this.state === "idle" || this.state === "done")) {
      this.state = "press";
      this.start = { x, y, t };
      this.dragged = false;
      return out;
    }
    if (this.pts.size === 2 && this.state !== "done" && this.state !== "rotate") {
      if (this.state === "drag") out.push({ type: "cancel" });
      const [a, b] = [...this.pts.entries()];
      this.rot = { a0: Math.atan2(b[1].y - a[1].y, b[1].x - a[1].x), last: 0, total: 0, ids: [a[0], b[0]] };
      this.state = "rotate";
      out.push({ type: "rotate-start", cx: (a[1].x + b[1].x) / 2, cy: (a[1].y + b[1].y) / 2 });
    }
    return out;
  }

  move(id: number, x: number, y: number, t: number): GestureEvent[] {
    if (!this.pts.has(id)) return [];
    this.pts.set(id, { x, y });
    const out = this.tick(t);
    switch (this.state) {
      case "press":
        if (Math.hypot(x - this.start.x, y - this.start.y) > this.slop) this.state = "swipe";
        break;
      case "drag":
        if (this.dragged || Math.hypot(x - this.start.x, y - this.start.y) > 2) {
          this.dragged = true;
          out.push({ type: "drag", x, y, x0: this.start.x, y0: this.start.y });
        }
        break;
      case "rotate": {
        const a = this.pts.get(this.rot.ids[0]), b = this.pts.get(this.rot.ids[1]);
        if (!a || !b) break;
        const ang = Math.atan2(b.y - a.y, b.x - a.x);
        // unwrap: the twist may go past 180 deg
        const d = wrap180(((ang - this.rot.a0) * 180) / Math.PI - this.rot.last);
        this.rot.total += d;
        this.rot.last = wrap180(this.rot.last + d);
        out.push({ type: "rotate", deg: this.rot.total, cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 });
        break;
      }
    }
    return out;
  }

  /** call regularly (and on every move): fires "hold" once HOLD_MS passed without moving */
  tick(t: number): GestureEvent[] {
    if (this.state === "press" && t - this.start.t >= this.holdMs) {
      this.state = "drag";
      return [{ type: "hold", x: this.start.x, y: this.start.y }];
    }
    return [];
  }

  up(id: number, x: number, y: number, t: number): GestureEvent[] {
    if (!this.pts.has(id)) return [];
    const out = this.tick(t);
    const p = this.pts.get(id) as Pt;
    this.pts.delete(id);
    switch (this.state) {
      case "press":
        if (Math.hypot(x - this.start.x, y - this.start.y) <= this.slop) out.push({ type: "tap", x: this.start.x, y: this.start.y });
        this.state = this.pts.size ? "done" : "idle";
        break;
      case "drag":
        // a hold released without moving is not a drag
        out.push(this.dragged ? { type: "drag-end", x, y, x0: this.start.x, y0: this.start.y } : { type: "cancel" });
        this.state = this.pts.size ? "done" : "idle";
        break;
      case "rotate": {
        const other = [...this.pts.values()][0] ?? p;
        const cx = (other.x + p.x) / 2, cy = (other.y + p.y) / 2;
        out.push(Math.abs(this.rot.total) >= MIN_TWIST_DEG ? { type: "rotate-end", deg: this.rot.total, cx, cy } : { type: "cancel" });
        this.state = this.pts.size ? "done" : "idle";
        break;
      }
      default:
        this.state = this.pts.size ? (this.state === "swipe" ? "swipe" : "done") : "idle";
    }
    return out;
  }

  /** the browser cancelled the touch (a system gesture, an alert) */
  cancelAll(): GestureEvent[] {
    const active = this.state === "drag" || this.state === "rotate";
    this.pts.clear();
    this.state = "idle";
    return active ? [{ type: "cancel" }] : [];
  }
}
